/**
 * The small window that opens before a function runs.
 *
 * Two jobs, in this order: say what the thing does, and show whether you have
 * what it needs. The checklist is the point — finding out a prerequisite was
 * missing *after* starting is the expensive order, because a pipeline stage
 * can be minutes in before it fails on an absent file.
 *
 * Every unmet requirement carries the button that fixes it, so the sheet is
 * somewhere to get unstuck rather than somewhere to be told no.
 */

import { useEffect, useRef } from "react";
import { Check, Loader2, X } from "lucide-react";

import { Button } from "@/components/ui/primitives";
import { DialogOverlay, DialogPanel, dialogPrimaryButtonClass, dialogSecondaryButtonClass } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { dispatchAction } from "@/shell/actions";
import { useAppStore } from "@/store/appStore";
import { useNavStore } from "./navStore";
import { blockingReason, requirementsFor, type Requirement } from "./launch";

function Row({ req }: { req: Requirement }) {
  return (
    <div className="flex items-start gap-2 py-1">
      <span
        className={cn(
          "mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full",
          req.met ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700",
        )}
      >
        {req.met ? <Check className="h-2.5 w-2.5" /> : <X className="h-2.5 w-2.5" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs text-neutral-800">{req.label}</span>
        {req.met && req.detail ? (
          <span className="block truncate text-[11px] text-neutral-500">{req.detail}</span>
        ) : null}
      </span>
      {!req.met && req.fix ? (
        <button
          type="button"
          className="shrink-0 text-[11px] text-ads-accent underline"
          onClick={() => dispatchAction(req.fix!.action)}
        >
          {req.fix.label}
        </button>
      ) : null}
    </div>
  );
}

export function LaunchSheet() {
  const pending = useNavStore((s) => s.pending);
  const dismiss = useNavStore((s) => s.dismiss);
  const confirm = useNavStore((s) => s.confirm);
  const skipNext = useNavStore((s) => s.skipConfirm);
  const setSkip = useNavStore((s) => s.setSkipConfirm);
  const busy = useNavStore((s) => s.busy);

  // A modal that only closes by clicking its backdrop is a trap for anyone
  // working from the keyboard, and the backdrop is the one place a mis-aimed
  // click is most likely. Escape closes it, and focus moves in on open so
  // Escape and Tab work without a click first.
  const panel = useRef<HTMLDivElement>(null);
  const open = pending != null;
  useEffect(() => {
    if (!open) return;
    const restore = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      // Cancelling a run that has already been handed off would lie about
      // stopping it, so the sheet stays put until `confirm` settles.
      if (!useNavStore.getState().busy) useNavStore.getState().dismiss();
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      restore?.focus?.();
    };
  }, [open]);

  const manifest = useAppStore((s) => s.currentCase);
  const engine = useAppStore((s) => s.systemStatus?.engine);
  const hasResults = useAppStore((s) => s.resultsAvailable === true);
  const selectedNodeId = useAppStore((s) => s.selectedNodeId);

  if (!pending) return null;

  const selected =
    manifest && selectedNodeId
      ? ([...manifest.images, ...manifest.masks].find((n) => n.id === selectedNodeId) ?? null)
      : null;

  const reqs = requirementsFor(pending.target, {
    manifest,
    engine,
    selectedVolumeLabel: selected ? selected.label : null,
    referenceLabel: null,
    hasResults,
  });
  const blocked = blockingReason(pending.target, reqs);

  return (
    <DialogOverlay
      className="z-50"
      role="dialog"
      aria-modal="true"
      aria-label={pending.target.title}
      onClick={dismiss}
    >
      <DialogPanel
        ref={panel}
        tabIndex={-1}
        className="w-full max-w-md"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-6 pb-4 pt-6">
          <h2 className="text-[17px] font-semibold tracking-tight text-ads-text">{pending.target.title}</h2>
          <p className="mt-1 text-[13px] leading-snug text-ads-muted">
            {pending.target.summary}
          </p>
        </div>

        <div className="px-6 pb-2">
          {pending.target.unavailable ? (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-900">
              {pending.target.unavailable}
            </p>
          ) : (
            <>
              <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
                What this needs
              </p>
              {reqs.map((req) => (
                <Row key={req.id} req={req} />
              ))}
              {/* The catalog's own prose prerequisites — the specific files,
                  which the checklist above cannot check for on its own. */}
              {pending.target.needs?.length ? (
                <p className="mt-2 text-[11px] leading-snug text-neutral-500">
                  Expects: {pending.target.needs.join(", ")}.
                </p>
              ) : null}
            </>
          )}
        </div>

        <div className="flex items-center gap-2 border-t border-black/[0.06] bg-neutral-50/60 px-6 py-4">
          <label className="flex items-center gap-1.5 text-[11px] text-neutral-500">
            <input
              type="checkbox"
              className="h-3 w-3 accent-ads-accent"
              checked={skipNext}
              onChange={(event) => setSkip(event.target.checked)}
            />
            Don’t show this again
          </label>
          <span className="flex-1" />
          <Button size="sm" className={dialogSecondaryButtonClass} onClick={dismiss}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            className={dialogPrimaryButtonClass}
            disabled={!!blocked || busy}
            title={blocked ?? undefined}
            onClick={() => void confirm()}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {pending.verb}
          </Button>
        </div>
        {blocked && !pending.target.unavailable ? (
          <p className="px-6 pb-4 text-right text-[11px] text-amber-700">{blocked}</p>
        ) : null}
      </DialogPanel>
    </DialogOverlay>
  );
}
